import { useEffect, useState } from "react";
import EntityManager from "@/components/EntityManager";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageCircle, FileText, Printer, Paperclip, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, API, formatApiError } from "@/lib/api";
import { today, inr, fmtDate } from "@/lib/format";
import { BRANCHES } from "@/lib/branches";
import { printVendorMaster, printVendorConsignments, printVendorVouchers, printVendorProfile } from "@/lib/print";
import { toast } from "sonner";

const KYC_REQUIRED = ["PAN Card", "GST Certificate", "Cancelled Cheque"];
const kycHave = (r) => {
  const labels = new Set((r.vendor_docs || []).map((d) => d.label));
  return KYC_REQUIRED.filter((l) => labels.has(l)).length;
};

const DOC_LABELS = ["PAN Card", "GST Certificate", "Cancelled Cheque", "Agreement", "Other"];

function VendorDocsDialog({ vendor, onClose }) {
  const token = localStorage.getItem("ntc_token");
  const [docs, setDocs] = useState([]);
  const [label, setLabel] = useState("PAN Card");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (vendor) { setDocs(vendor.vendor_docs || []); setLabel("PAN Card"); } }, [vendor]);
  if (!vendor) return null;
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("label", label);
      fd.append("file", file);
      const res = await api.post(`/vendors/${vendor.id}/docs`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((d) => [...d, res.data]);
      toast.success(`${label} attached`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (p) => {
    try {
      await api.delete(`/vendors/${vendor.id}/docs`, { params: { path: p } });
      setDocs((d) => d.filter((x) => x.path !== p));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[520px]" data-testid="vendor-docs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Paperclip className="h-4 w-4 text-primary" /> KYC Documents — {vendor.name}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 py-1">
          <div className="flex flex-wrap gap-1.5">
            {DOC_LABELS.map((l) => (
              <button
                key={l}
                type="button"
                className={`rounded-full border px-3 py-1 text-xs font-medium ${label === l ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                onClick={() => setLabel(l)}
                data-testid={`vendor-doc-label-${l.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l}
              </button>
            ))}
          </div>
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-4 text-sm text-slate-600 hover:bg-slate-50">
            <Paperclip className="h-4 w-4" />
            {busy ? "Uploading…" : `Attach ${label} (JPG / PNG / PDF, max 10MB)`}
            <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" disabled={busy} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="vendor-doc-file-input" />
          </label>
          <div className="max-h-56 space-y-1.5 overflow-y-auto" data-testid="vendor-docs-list">
            {docs.length ? docs.map((d) => (
              <div key={d.path} className="flex items-center gap-2 rounded-md border border-border bg-slate-50 px-2.5 py-1.5" data-testid="vendor-doc-row">
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">{d.label || "Doc"}</span>
                <a href={`${API}/files/${d.path}?auth=${token}`} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-sm text-blue-700 underline" data-testid="vendor-doc-link">
                  {d.filename || d.path.split("/").pop()}
                </a>
                <span className="text-[10px] text-muted-foreground">{d.at ? fmtDate(String(d.at).slice(0, 10)) : ""}</span>
                <button type="button" className="text-slate-400 hover:text-red-600" onClick={() => remove(d.path)} data-testid="vendor-doc-delete">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )) : (
              <p className="text-xs text-muted-foreground">No documents yet — attach PAN, GST certificate and a cancelled cheque so KYC is always on hand.</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const exportVendorsCsv = (list) => {
  const headers = ["Sl No.", "Branch", "Vendor/Broker Code", "Vendor/Broker Name", "Vendor/Broker Type", "Contact Person", "Mobile No.", "Email", "GSTIN", "PAN", "TAN", "Account Holder Name", "Account Number", "Bank Name", "IFSC", "Bank Address", "Address"];
  const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = [headers.join(",")];
  list.forEach((r, i) => lines.push([i + 1, r.branch || "", r.vendor_code || "", r.name || "", r.type || "", r.contact_person || "", r.phone || "", r.email || "", r.gstin || "", r.pan || "", r.tan || "", r.account_holder || "", r.account_no || "", r.bank_name || "", r.ifsc || "", r.bank_address || "", r.address || ""].map(cell).join(",")));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ntc-vendors-${today()}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export default function VendorMaster() {
  const navigate = useNavigate();
  const [balances, setBalances] = useState({});
  const [stats, setStats] = useState({});
  const [podScore, setPodScore] = useState({});
  useEffect(() => {
    api.get("/hire-challans").then((r) => {
      const m = {};
      (r.data || []).forEach((c) => {
        const k = String(c.vendor_broker_name || c.owner_name || "").trim().toLowerCase();
        if (!k) return;
        m[k] = m[k] || { back: 0, out: 0, sumDays: 0 };
        const ld = String(c.loading_date || c.date || "").slice(0, 10);
        if (c.pod_ack_received) {
          m[k].back++;
          if (c.pod_ack_date && /^\d{4}-\d{2}-\d{2}$/.test(ld)) m[k].sumDays += Math.max(Math.floor((new Date(c.pod_ack_date) - new Date(ld)) / 86400000), 0);
        } else {
          m[k].out++;
        }
      });
      const out = {};
      Object.entries(m).forEach(([k, v]) => {
        out[k] = { ...v, avg: v.back ? Math.round((v.sumDays / v.back) * 10) / 10 : null };
      });
      setPodScore(out);
    }).catch(() => {});
  }, []);
  const podTag = (r) => {
    const s = podScore[String(r.name || "").trim().toLowerCase()];
    if (!s || s.avg == null) return null;
    const slow = s.avg > 15;
    return (
      <span
        className={`whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-bold ${slow ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}
        title={`Average ${s.avg} day(s) from loading to signed POD return · ${s.back} back${s.out ? ` · ${s.out} still out` : ""}`}
        data-testid={slow ? "vendor-slow-pod-tag" : "vendor-fast-pod-tag"}
      >
        {slow ? `Slow POD · ${s.avg}d` : `Fast POD · ${s.avg}d`}
      </span>
    );
  };
  const [drill, setDrill] = useState(null);
  const [docsVendor, setDocsVendor] = useState(null);
  const [drillRows, setDrillRows] = useState(null);
  const [payDrill, setPayDrill] = useState(null);
  const [payRows, setPayRows] = useState(null);
  const openPayDrill = async (vendor) => {
    setPayDrill(vendor);
    setPayRows(null);
    try {
      const res = await api.get("/vouchers");
      const n = String(vendor.name || "").trim().toLowerCase();
      setPayRows(
        (res.data || [])
          .filter((v) => String(v.party_name || "").trim().toLowerCase() === n)
          .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")))
      );
    } catch {
      setPayRows([]);
    }
  };
  const vendorWa = (vendor, text) => {
    const d = String(vendor?.phone || "").replace(/\D/g, "");
    if (!d) {
      toast.error(`No mobile number saved for ${vendor?.name || "this vendor"} in Vendor Master.`);
      return;
    }
    window.open(`https://wa.me/${d.length === 10 ? "91" + d : d}?text=${encodeURIComponent(text)}`, "_blank");
  };
  const waConsignments = (vendor, rows) => {
    const total = rows.reduce((a, s) => a + (Number(s.lorry_hire) || 0), 0);
    const lines = [
      `🚛 *NTC Consignment Statement — ${vendor.name}${vendor.vendor_code ? ` (${vendor.vendor_code})` : ""}*`,
      `${rows.length} consignment(s) · As on ${fmtDate(today())}`,
      "",
      ...rows.slice(0, 15).map((s) => `LR ${s.lr_no || s.consignment_no || "—"} · ${(s.lr_date || s.dispatch_date) ? fmtDate(s.lr_date || s.dispatch_date) : "—"} · ${s.origin || "—"}→${s.destination || "—"} · Hire ₹${Number(s.lorry_hire || 0).toLocaleString("en-IN")}`),
      rows.length > 15 ? `…and ${rows.length - 15} more` : "",
      "",
      `*Total Lorry Hire: ₹${total.toLocaleString("en-IN")}*`,
      "Kindly verify and confirm for reconciliation.",
      "— Nisha Transport Corporation",
    ].filter((l) => l !== "");
    vendorWa(vendor, lines.join("\n"));
  };
  const waVouchers = (vendor, rows) => {
    const total = rows.reduce((a, v) => a + (Number(v.amount) || 0), 0);
    const lines = [
      `🧾 *NTC Payment Statement — ${vendor.name}${vendor.vendor_code ? ` (${vendor.vendor_code})` : ""}*`,
      `${rows.length} voucher(s) · As on ${fmtDate(today())}`,
      "",
      ...rows.slice(0, 15).map((v) => `DV ${String(v.voucher_no || "—").replace(/^DV[-\s]?/i, "")} · ${v.date ? fmtDate(v.date) : "—"} · ${v.payment_mode || "Cash"}${v.cheque_no ? ` (Chq ${v.cheque_no})` : ""} · ₹${Number(v.amount || 0).toLocaleString("en-IN")}`),
      rows.length > 15 ? `…and ${rows.length - 15} more` : "",
      "",
      `*Total Paid: ₹${total.toLocaleString("en-IN")}*`,
      "Kindly verify and confirm for reconciliation.",
      "— Nisha Transport Corporation",
    ].filter((l) => l !== "");
    vendorWa(vendor, lines.join("\n"));
  };
  const openDrill = async (vendor) => {
    setDrill(vendor);
    setDrillRows(null);
    try {
      const res = await api.get("/shipments");
      const n = String(vendor.name || "").trim().toLowerCase();
      const c = String(vendor.vendor_code || "").trim().toLowerCase();
      setDrillRows(
        (res.data || [])
          .filter((s) => {
            const sn = String(s.vendor_name || "").trim().toLowerCase();
            const sc = String(s.vendor_code || "").trim().toLowerCase();
            return (n && sn === n) || (c && sc === c);
          })
          .sort((a, b) => String(b.lr_date || b.dispatch_date || "").localeCompare(String(a.lr_date || a.dispatch_date || "")))
      );
    } catch {
      setDrillRows([]);
    }
  };
  useEffect(() => {
    api.get("/vendors/stats").then((r) => setStats(r.data || {})).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/reports/vendor-ledger").then((r) => {
      const m = {};
      (r.data?.rows || []).forEach((row) => { m[String(row.vendor || "").trim().toLowerCase()] = Number(row.balance) || 0; });
      setBalances(m);
    }).catch(() => {});
  }, []);
  return (
    <>
    <EntityManager
      title="Vendor Master / Broker"
      subtitle="Vehicle owners, brokers and vendors you hire vehicles from — with contact, GST and bank details."
      endpoint="vendors"
      entityName="Vendor"
      selectable
      filters={[
        {
          key: "q",
          label: "Search",
          type: "text",
          placeholder: "Name / code / phone / GSTIN / type",
          match: (r, v) => {
            const q = String(v || "").trim().toLowerCase();
            if (!q) return true;
            return [r.name, r.vendor_code, r.type, r.contact_person, r.phone, r.email, r.gstin, r.pan, r.branch, r.bank_name, r.account_no]
              .some((x) => String(x || "").toLowerCase().includes(q));
          },
        },
      ]}
      exportCsv={exportVendorsCsv}
      onPrintSelected={printVendorMaster}
      defaults={{
        name: "",
        vendor_code: "",
        type: "Vendor",
        branch: "",
        contact_person: "",
        phone: "",
        email: "",
        gstin: "",
        pan: "",
        tan: "",
        bank_name: "",
        account_holder: "",
        account_no: "",
        ifsc: "",
        bank_address: "",
        address: "",
      }}
      fields={[
        { key: "name", label: "Vendor/Broker Name", type: "text", placeholder: "Sardar Transport" },
        { key: "vendor_code", label: "Vendor/Broker Code", type: "text", placeholder: "Auto-generated (N2001 series) — leave blank" },
        { key: "type", label: "Vendor/Broker Type", type: "text", suggestions: ["Vendor", "Broker", "Vehicle Owner"], placeholder: "Vendor / Broker / Vehicle Owner — or type your own" },
        { key: "branch", label: "Branch", type: "select", options: BRANCHES, placeholder: "Select branch" },
        { key: "contact_person", label: "Contact Person", type: "text", placeholder: "Owner name" },
        { key: "phone", label: "Mobile No.", type: "text", placeholder: "+91 98765 43210" },
        { key: "email", label: "Email", type: "text", placeholder: "vendor@email.com" },
        { key: "gstin", label: "GSTIN", type: "text", placeholder: "33ABCDE1234F1Z5" },
        { key: "pan", label: "PAN", type: "text", placeholder: "ABCDE1234F" },
        { key: "tan", label: "TAN", type: "text", placeholder: "CHET12345F" },
        { key: "account_holder", label: "Account Holder Name", type: "text", placeholder: "Name as per bank account" },
        { key: "account_no", label: "Account Number", type: "text", placeholder: "1234567890" },
        { key: "bank_name", label: "Bank Name", type: "text", placeholder: "Punjab National Bank" },
        { key: "ifsc", label: "IFSC code", type: "text", placeholder: "PUNB0597800 — auto-fills bank name & address",
          autoFill: async (v, form) => {
            const code = String(v || "").trim().toUpperCase();
            if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(code)) return null;
            try {
              const res = await fetch(`https://ifsc.razorpay.com/${code}`);
              if (!res.ok) { toast.error(`IFSC ${code} not found`); return { ifsc: code }; }
              const d = await res.json();
              toast.success(`${d.BANK} — ${d.BRANCH} auto-filled`);
              return {
                ifsc: code,
                ...(d.BANK ? { bank_name: d.BANK } : {}),
                ...(d.ADDRESS || d.BRANCH ? { bank_address: [d.BRANCH, d.ADDRESS].filter(Boolean).join(", ") } : {}),
              };
            } catch { return { ifsc: code }; }
          } },
        { key: "bank_address", label: "Bank Address", type: "text", placeholder: "Branch address of the bank" },
        { key: "address", label: "Vendor/Broker Address", type: "textarea", placeholder: "Full address of the vendor/broker" },
      ]}
      columns={[
        { key: "_sl", label: "SL No.", render: (r, idx) => (typeof idx === "number" ? idx + 1 : "—") },
        { key: "branch", label: "Branch", render: (r) => r.branch || "—" },
        { key: "name", label: "Vendor/Broker Name", render: (r) => {
          const have = kycHave(r);
          return (
            <span className="inline-flex items-center gap-1.5">
              {r.name}
              {have === 3 ? (
                <span className="whitespace-nowrap rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700" title="PAN, GST certificate and cancelled cheque all attached" data-testid="kyc-ok-chip">KYC ✓</span>
              ) : have > 0 ? (
                <span className="whitespace-nowrap rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700" title={`Missing: ${KYC_REQUIRED.filter((l) => !(r.vendor_docs || []).some((d) => d.label === l)).join(", ")}`} data-testid="kyc-partial-chip">KYC {have}/3</span>
              ) : null}
              {podTag(r)}
            </span>
          );
        } },
        { key: "vendor_code", label: "Vendor/Broker Code", render: (r) => r.vendor_code || "—" },
        { key: "type", label: "Vendor/Broker Type" },
        { key: "contact_person", label: "Contact Person" },
        { key: "phone", label: "Mobile No." },
        { key: "_consignments", label: "Consignments", render: (r) => {
          const s = stats[r.id];
          if (!s) return <span className="text-xs text-muted-foreground">—</span>;
          return (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (s.consignments) openDrill(r); }}
              className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold ${s.consignments ? "cursor-pointer bg-blue-100 text-blue-700 hover:bg-blue-200" : "bg-slate-100 text-slate-500"}`}
              title={s.consignments ? `See ${r.name}'s LR list` : "No consignments yet"}
              data-testid="vendor-consignments-chip"
            >
              {s.consignments}
            </button>
          );
        } },
        { key: "_payments", label: "Payments", render: (r) => {
          const s = stats[r.id];
          if (!s) return <span className="text-xs text-muted-foreground">—</span>;
          if (!s.payments_count) return <span className="text-xs text-muted-foreground">—</span>;
          return (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); openPayDrill(r); }}
              className="inline-flex cursor-pointer rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700 hover:bg-emerald-200"
              title={`See ${r.name}'s voucher list`}
              data-testid="vendor-payments-chip"
            >
              {inr(s.payments_total)} · {s.payments_count}v
            </button>
          );
        } },
        { key: "_hire_due", label: "Hire Due", render: (r) => {
          const s = stats[r.id];
          if (!s || !s.hire_total) return <span className="text-xs text-muted-foreground">—</span>;
          const due = s.hire_due || 0;
          if (due > 0)
            return (
              <span className="flex items-center gap-1">
                <span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-700" title={`Hire ${inr(s.hire_total)} − Paid ${inr(s.payments_total)}`} data-testid="vendor-hire-due-chip">
                  {inr(due)} due
                </span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); navigate(`/vouchers?pay_to=${encodeURIComponent(r.name)}&amount=${Math.round(due)}&code=${encodeURIComponent(r.vendor_code || "")}`); }}
                  className="inline-flex rounded-full border border-blue-300 bg-blue-50 px-2 py-0.5 text-[11px] font-bold text-blue-700 hover:bg-blue-100"
                  title={`Create a debit voucher of ${inr(due)} for ${r.name}`}
                  data-testid="vendor-pay-now-btn"
                >
                  Pay Now
                </button>
              </span>
            );
          return (
            <span className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700" title={`Hire ${inr(s.hire_total)} − Paid ${inr(s.payments_total)}`} data-testid="vendor-hire-due-chip">
              {due < 0 ? `Settled · ${inr(-due)} extra` : "Settled"}
            </span>
          );
        } },
        { key: "_outstanding", label: "Outstanding", render: (r) => {
          const bal = balances[String(r.name || "").trim().toLowerCase()];
          if (bal === undefined) return <span className="text-xs text-muted-foreground">—</span>;
          const cls = bal > 0 ? "bg-red-100 text-red-700 hover:bg-red-200" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200";
          return (
            <button
              type="button"
              className={`inline-flex cursor-pointer rounded-full px-2 py-0.5 text-[11px] font-bold ${cls}`}
              title={`Open ${r.name || "vendor"}'s ledger statement`}
              onClick={(e) => { e.stopPropagation(); navigate(`/vendor-ledger?vendor=${encodeURIComponent(r.name || "")}`); }}
              data-testid="vendor-outstanding-chip"
            >
              {bal > 0 ? `${inr(bal)} due` : "Clear"}
            </button>
          );
        } },
        { key: "gstin", label: "GSTIN" },
        { key: "tan", label: "TAN", render: (r) => r.tan || "—" },
        { key: "address", label: "Vendor/Broker Address", render: (r) => r.address || "—" },
      ]}
      confirmSave={(form, rows, editing) => {
        const others = (rows || []).filter((r) => !editing || r.id !== editing.id);
        const code = String(form.vendor_code || "").trim().toLowerCase();
        const ph = String(form.phone || "").replace(/\D/g, "").slice(-10);
        const dupCode = code ? others.find((r) => String(r.vendor_code || "").trim().toLowerCase() === code) : null;
        const dupPh = ph ? others.find((r) => String(r.phone || "").replace(/\D/g, "").slice(-10) === ph) : null;
        if (!dupCode && !dupPh) return true;
        const what = [
          dupCode ? `Code "${form.vendor_code}" is already used by ${dupCode.name}` : null,
          dupPh ? `Mobile "${form.phone}" is already used by ${dupPh.name}` : null,
        ].filter(Boolean).join("\n");
        return window.confirm(`⚠ Duplicate found:\n\n${what}\n\nSave anyway?`);
      }}
      rowActions={(r) => {
        const ph = String(r.phone || "").replace(/\D/g, "");
        const msg = `Hello ${r.contact_person || r.name || ""}, this is Nisha Transport Corporation.`;
        const profileMsg = [
          "*Nisha Transport Corporation — Vendor Profile Confirmation*",
          "",
          `Dear ${r.contact_person || r.name || "Partner"},`,
          "Please confirm the details we have on record before we process payments:",
          "",
          `Vendor/Broker Code: ${r.vendor_code || "—"}`,
          `Name: ${r.name || "—"}`,
          r.type ? `Type: ${r.type}` : "",
          r.gstin ? `GSTIN: ${r.gstin}` : "",
          r.pan ? `PAN: ${r.pan}` : "",
          "",
          "*Bank details for payment:*",
          `Account Holder: ${r.account_holder || "—"}`,
          `Account No: ${r.account_no || "—"}`,
          `Bank: ${r.bank_name || "—"}`,
          `IFSC: ${r.ifsc || "—"}`,
          "",
          'Reply *CONFIRMED* if everything is correct, or send the corrections.',
          "— Nisha Transport Corporation",
        ].filter((l) => l !== "").join("\n");
        return (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-slate-600"
              title={`Print profile card for ${r.name || "vendor"}`}
              onClick={() => {
                const tk = localStorage.getItem("ntc_token");
                const docs = (r.vendor_docs || []).map((d) => ({ url: `${API}/files/${d.path}?auth=${tk}`, label: d.label || "Doc", filename: d.filename || "", isPdf: /\.pdf$/i.test(d.path) }));
                printVendorProfile({ ...r, _docUrls: docs, _podScore: podScore[String(r.name || "").trim().toLowerCase()] });
              }}
              data-testid="vendor-profile-print-btn"
            >
              <Printer className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-emerald-700"
              title={`WhatsApp profile & bank details to ${r.name || "vendor"} for confirmation`}
              onClick={() => window.open(ph ? `https://wa.me/91${ph.slice(-10)}?text=${encodeURIComponent(profileMsg)}` : `https://wa.me/?text=${encodeURIComponent(profileMsg)}`, "_blank")}
              data-testid="vendor-profile-wa-btn"
            >
              <MessageCircle className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-amber-600"
              title={`KYC documents for ${r.name || "vendor"} (PAN, GST, cancelled cheque)`}
              onClick={() => setDocsVendor(r)}
              data-testid="vendor-docs-btn"
            >
              <Paperclip className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-primary"
              title={`Open ledger statement for ${r.name || "vendor"}`}
              onClick={() => navigate(`/vendor-ledger?vendor=${encodeURIComponent(r.name || "")}`)}
              data-testid="vendor-ledger-btn"
            >
              <FileText className="h-4 w-4" />
            </Button>
            {ph ? (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-emerald-600"
                title={`WhatsApp ${r.name || "vendor"} (${r.phone})`}
                onClick={() => window.open(`https://wa.me/91${ph.slice(-10)}?text=${encodeURIComponent(msg)}`, "_blank")}
                data-testid="vendor-wa-btn"
              >
                <MessageCircle className="h-4 w-4" />
              </Button>
            ) : null}
          </>
        );
      }}
    />
    {docsVendor && <VendorDocsDialog vendor={docsVendor} onClose={() => setDocsVendor(null)} />}
    <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[760px]" data-testid="vendor-drill-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {drill?.name} — Consignments {drill?.vendor_code ? `(${drill.vendor_code})` : ""}
          </DialogTitle>
        </DialogHeader>
        {drillRows === null ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
        ) : drillRows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No consignments found for this vendor.</p>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {drillRows.length} consignment(s) · Lorry hire total {inr(drillRows.reduce((a, s) => a + (Number(s.lorry_hire) || 0), 0))}
              </p>
              <span className="flex gap-2">
                <Button size="sm" variant="outline" className="h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => waConsignments(drill, drillRows)} data-testid="vendor-drill-wa-btn">
                  <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> WhatsApp
                </Button>
                <Button size="sm" variant="outline" className="h-8" onClick={() => printVendorConsignments(drill, drillRows)} data-testid="vendor-drill-print-btn">
                  <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
                </Button>
              </span>
            </div>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {["LR No.", "LR Date", "From", "Destination", "Customer", "Vehicle No.", "Lorry Hire", "Status"].map((h) => (
                      <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {drillRows.map((s) => (
                    <tr key={s.id} className="border-t" data-testid="vendor-drill-row">
                      <td className="px-3 py-1.5 font-semibold">{s.lr_no || s.consignment_no || "—"}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap">{(s.lr_date || s.dispatch_date) ? fmtDate(s.lr_date || s.dispatch_date) : "—"}</td>
                      <td className="px-3 py-1.5">{s.origin || "—"}</td>
                      <td className="px-3 py-1.5">{s.destination || "—"}</td>
                      <td className="px-3 py-1.5">{s.customer_name || "—"}</td>
                      <td className="px-3 py-1.5">{s.vehicle_no || "—"}</td>
                      <td className="px-3 py-1.5 font-semibold">{inr(Number(s.lorry_hire) || 0)}</td>
                      <td className="px-3 py-1.5">{s.status || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
    <Dialog open={!!payDrill} onOpenChange={(o) => !o && setPayDrill(null)}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[700px]" data-testid="vendor-pay-drill-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{payDrill?.name} — Voucher Payments</DialogTitle>
        </DialogHeader>
        {payRows === null ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
        ) : payRows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No vouchers found for this vendor.</p>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {payRows.length} voucher(s) · Total paid {inr(payRows.reduce((a, v) => a + (Number(v.amount) || 0), 0))}
              </p>
              <span className="flex gap-2">
                <Button size="sm" variant="outline" className="h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => waVouchers(payDrill, payRows)} data-testid="vendor-pay-drill-wa-btn">
                  <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> WhatsApp
                </Button>
                <Button size="sm" variant="outline" className="h-8" onClick={() => printVendorVouchers(payDrill, payRows)} data-testid="vendor-pay-drill-print-btn">
                  <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
                </Button>
              </span>
            </div>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {["Voucher No.", "Date", "Branch", "Category", "Payment Mode", "Amount"].map((h) => (
                      <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payRows.map((v) => (
                    <tr key={v.id} className="border-t" data-testid="vendor-pay-drill-row">
                      <td className="px-3 py-1.5 font-semibold">{String(v.voucher_no || "—").replace(/^DV[-\s]?/i, "")}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap">{v.date ? fmtDate(v.date) : "—"}</td>
                      <td className="px-3 py-1.5">{v.branch || "Chennai"}</td>
                      <td className="px-3 py-1.5">{v.category || "—"}</td>
                      <td className="px-3 py-1.5">{v.payment_mode || "Cash"}{v.cheque_no ? ` (Chq ${v.cheque_no})` : ""}</td>
                      <td className="px-3 py-1.5 font-semibold">{inr(Number(v.amount) || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
